import {
  WSAlert,
  WSControlGroup,
  WSDropFileInput,
  WSElement,
  WSFlexBox,
  WSForm,
  WSIcon,
  WSList,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import { SignerRole } from "@wingspanhq/signed-documents/dist/lib/interfaces";
import React from "react";
import { useFormContext } from "react-hook-form";

import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";
import { useMutationMemberPrivateFileCreate } from "../../../../../query/files/mutations";
import { useQueryMemberPrivateFile } from "../../../../../query/files/queries";
import { openInNewTab } from "../../../../../shared/utils/openInNewTab";
import { useESignatureAgreementTemplatesInfo } from "../../../../../userFeatures/useESignatureAgreementTemplatesInfo";
import styles from "../../../components/CreateRequirementDefinitionForm/styles.module.scss";
import { useSignatureCost } from "../../../hooks/useSignatureCost";
import { RequirementDefinitionSignatureFormValues } from "../../../types";

import { useMutationCreateSignatureRequirementDefinitionDataSource } from "./useMutationCreateSignatureRequirementDefinitionDataSource";

const FilePanel: React.FC<{ fileId: string; onRemove(): void }> = ({
  fileId,
  onRemove
}) => {
  const privateFile = useQueryMemberPrivateFile(fileId);

  const file = privateFile.data;

  return privateFile.isError && privateFile.error ? (
    <WSErrorMessage contextKey="Other" error={privateFile.error} />
  ) : (
    <WSElement
      className={styles.selectedFile}
      p="XL"
      shimmer={privateFile.isLoading}
    >
      <WSFlexBox.CenterY wrap="nowrap">
        <WSText className={styles.selectedFileName} singleLine>
          {file?.filename}
        </WSText>
        <WSFlexBox>
          <WSIcon
            block
            color="gray500"
            mx="M"
            name="exit"
            size="M"
            onClick={() => {
              onRemove();
            }}
          />
        </WSFlexBox>
      </WSFlexBox.CenterY>
    </WSElement>
  );
};

export const CreateSignaturePartialForm: React.FC = () => {
  const { setValue } = useFormContext();

  const customInfo = useESignatureAgreementTemplatesInfo();
  const signatureCost = useSignatureCost();
  const [createSignedDocumentTemplate, createSignedDocumentTemplateMeta] =
    useMutationCreateSignatureRequirementDefinitionDataSource();

  const [uploadFile, uploadFileMeta] = useMutationMemberPrivateFileCreate({
    hidden: true
  });

  return (
    <WSList gap="XL">
      <WSAlert
        action={
          customInfo?.actionTitle
            ? {
                onClick: () => openInNewTab(customInfo.actionLink),
                text: customInfo.actionTitle
              }
            : undefined
        }
        theme="info"
        title={
          signatureCost
            ? "About e-signature billable requirements"
            : "About e-Signature requirements"
        }
      >
        Contractor(s) assigned with this requirement will be asked to e-sign the
        document uploaded here.
        {signatureCost ? (
          <>
            <WSElement mt="M" />
            E-signature is a billable requirement and will be billed to you, the
            client, at {signatureCost.amount} for every successful signature.
          </>
        ) : null}
        {customInfo?.description ? (
          <>
            <WSElement mt="M" />
            {customInfo.description}
          </>
        ) : null}
      </WSAlert>

      <WSForm.Field
        component={WSTextInput}
        componentProps={{
          hidden: true
        }}
        invisible={true}
        label="File"
        name="fileId"
      />

      <WSForm.Field
        component={WSTextInput}
        componentProps={{
          hidden: true
        }}
        invisible={true}
        label="Template ID"
        name="signedDocumentTemplateId"
      />

      <WSForm.Values names={["fileId", "requiredSignatures"]}>
        {({
          fileId,
          requiredSignatures
        }: {
          fileId?: RequirementDefinitionSignatureFormValues["fileId"];
          requiredSignatures?: RequirementDefinitionSignatureFormValues["requiredSignatures"];
        }) => (
          <>
            <WSForm.Field
              component={WSControlGroup}
              hidden={!!fileId}
              componentProps={{
                disabled: !!fileId,
                options: [
                  {
                    label: "Contractor signature required only",
                    value: "collaborator"
                  },
                  {
                    label:
                      "Both contractor and company signatures are required",
                    value: "mutual"
                  }
                ],
                required: true,
                type: "radio"
              }}
              label="Required signatures"
              name="requiredSignatures"
            />

            <WSElement
              invisible={!!fileId}
              shimmer={
                uploadFileMeta.isLoading ||
                createSignedDocumentTemplateMeta.isLoading
              }
            >
              <WSText.ParagraphSm color="gray500" mb="S">
                Upload e-Signature file *
              </WSText.ParagraphSm>
              <WSDropFileInput
                accept=".pdf"
                buttonProps={{
                  type: "button"
                }}
                buttonText="Browse files"
                name="dropFileInput"
                onDropAccepted={async accepted => {
                  const data = new FormData();
                  data.append("file", accepted[0]);
                  await uploadFile(data, {
                    onSuccess: response => {
                      const file = response[0];

                      createSignedDocumentTemplate(
                        {
                          fileId: file.fileId,
                          roles:
                            requiredSignatures === "collaborator"
                              ? [SignerRole.Payee]
                              : [SignerRole.Payee, SignerRole.Payer],
                          title: file.filename,
                          userId: file.memberId
                        },
                        {
                          onError: error => {
                            setValue("fileId", "");
                            setValue("signedDocumentTemplateId", "");
                          },
                          onSuccess: response => {
                            setValue(
                              "signedDocumentTemplateId",
                              response.signedDocumentTemplateId
                            );
                            setValue("fileId", file.fileId);
                          }
                        }
                      );
                    }
                  });
                }}
              />
            </WSElement>

            {fileId ? (
              <WSList gap="S">
                <FilePanel
                  fileId={fileId}
                  onRemove={() => {
                    setValue("fileId", "");
                  }}
                />

                <WSText.ParagraphSm color="gray500">
                  Required signatures:{" "}
                  {requiredSignatures === "collaborator"
                    ? "Contractor"
                    : "Both contractor and company"}
                </WSText.ParagraphSm>
              </WSList>
            ) : null}
          </>
        )}
      </WSForm.Values>
      <WSText.ParagraphXs color="gray400" weight="book">
        Wingspan can auto-fill known info like names, emails, custom fields
        which you can configure in the next step.
      </WSText.ParagraphXs>

      <WSErrorMessage contextKey="Other" error={uploadFileMeta.error} />
    </WSList>
  );
};
